import { Outlet, useLocation } from "react-router";

import { useTactileSurface, useTelegramBackButton } from "@/shared/telegram";
import { BottomNav } from "@/widgets/bottom-nav";
import { DemoRibbon } from "@/widgets/demo-ribbon";

import { supportsViewTransition } from "./useRouteTransition";

/**
 * Каркас экрана. Панель навигации присутствует на всех экранах без исключения —
 * так задано документацией компонента в Figma (11:84).
 *
 * max-w-[430px] нужен для десктопного клиента Telegram и для отладки в
 * браузере: макет нарисован под 390, без ограничения контент растянулся бы
 * на всю ширину окна.
 *
 * Движение (FE-13): переходом между экранами занимается useRouteTransition()
 * через View Transitions. Там, где их нет, вход отрабатывает CSS-анимацией
 * `page-enter`, а `key` по пути заставляет ветку пересобраться — иначе при
 * навигации между экранами одного компонента (`/robots/1` → `/robots/2`)
 * анимации бы не было. Панель навигации получает собственное имя перехода:
 * иначе она уезжала бы вместе с содержимым, хотя остаётся на месте.
 */
export function AppLayout() {
  useTelegramBackButton();
  // Тактильный слой (FE-14): один слушатель на весь мини-апп, отклик получают
  // все элементы с утилитами `press`/`press-row` и вся нативная интерактивная
  // разметка. Подробности — в shared/telegram/useTactileSurface.ts.
  useTactileSurface();
  const location = useLocation();
  const fallback = !supportsViewTransition();

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-[430px] flex-col">
      {/* Точечная сетка макета. Отдельный фиксированный слой, а не
          псевдоэлемент на контейнере: экран скроллится, и сетка на нём
          растянулась бы на всю высоту документа, потеряв шаг 13px. */}
      <div aria-hidden="true" className="backdrop-grid pointer-events-none fixed inset-0 z-0" />

      {/* Верхний отступ учитывает вырез: собственной шапки у приложения нет —
          её рисует Telegram, — поэтому первый же заголовок экрана оказался бы
          вплотную к выступу. */}
      <main
        key={fallback ? location.pathname : undefined}
        className={[
          // `flex flex-col` — чтобы экран мог растянуться на всю высоту и
          // прижать блок к низу (`mt-auto`). Без этого `min-height: 100%` у
          // содержимого не разрешается: у main нет заданной высоты, только
          // растянутая флексом.
          "relative z-1 flex flex-1 flex-col pt-[env(safe-area-inset-top)]",
          "pb-[calc(var(--nav-height)+var(--nav-gap)*2+env(safe-area-inset-bottom))]",
          fallback ? "page-enter" : "",
        ].join(" ")}
      >
        {/* Маркировка демо-режима (доработки §3.1). Здесь, а не на страницах:
            §3.1 требует её «на всех экранах без исключения», а плашку, которую
            каждый экран рисует сам, рано или поздно забудут на новом. */}
        <DemoRibbon />
        <Outlet />
      </main>

      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-10 px-[var(--nav-inset)] pb-[max(var(--nav-gap),env(safe-area-inset-bottom))] [view-transition-name:bottom-nav]">
        <div className="pointer-events-auto">
          <BottomNav />
        </div>
      </div>
    </div>
  );
}
